import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  getMaterialRequest,
  getMaterialRequestById,
  getParticularPage,
  getNext,
  getPrevious,
  handleSearch,
  createMaterialRequest,
} from "./thunk";
import { NepaliDate } from "@zener/nepali-datepicker-react";
import { dateFormater } from "../../../../Utils/dateFormater";
import { materialRequestsFrom } from "../../../../Utils/constants";

export const initialState = {
  materialRequests: [],
  edit: false,
  materialRequest: null,
  count: 0,
  pageNo: 1,
  next: null,
  previous: null,
  loadingCreate: false,
  loading: false,
  loadingUpdated: false,
  loadingMaterialRequest: false,
  loadingNext: false,
  saveAndContinue: false,
  materialRequestDetails: [
    {
      product: null,
      vendor: null,
      closingStock: "",
      description: "",
      actualQuantity: "",
      unit: "",
      altQuantity: "",
      altUnit: null,
      rate: "",
      amount: "",
      requiredDate: dateFormater(new NepaliDate()),
      // requiredDateAd: new NepaliDate().toADasDate().toISOString(),
      unique: uuidv4(),
    },
  ],
  showCreateModal: false,

  // New state properties for form persistence
  formData: {
    indentNo: "",
    isFromHo: materialRequestsFrom[0],
    requiredDateBs: new NepaliDate(),
    voucherDateBs: new NepaliDate(),
    // transactionDate: new NepaliDate(),
    requesterName: "",
    department: "",
    receiverBranch: "",
    placeOfSupply: "",
    branch: null,
    narration: "",
    discountAmount: "",
    grandTotal: "",
    documentsAttach: [],
  },
  activeBaseUnitId: null,
};

export const materialRequestSlice = createSlice({
  name: "materialRequest",
  initialState,
  reducers: {
    setShowCreateModal: (state, action) => {
      state.showCreateModal = action.payload;
    },
    setSaveAndContinue: (state, { payload }) => {
      state.saveAndContinue = payload;
    },
    goToPage: (state, { payload }) => {
      state.pageNo = payload;
    },
    materialRequestDetailsUpdated: (state, action) => {
      state.materialRequestDetails = action.payload;
    },

    clearMaterialRequestDetails: (state) => {
      state.materialRequestDetails = [
        {
          product: null,
          vendor: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate()),
          unique: uuidv4(),
        },
      ];
    },
    clearAllMaterialRequest: (state) => {
      state.edit = false;
      state.loading = false;
      state.loadingUpdated = false;
      state.loadingMaterialRequest = false;
      state.materialRequest = null;
      state.saveAndContinue = false;
      state.materialRequestDetails = [
        {
          product: null,
          vendor: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate()),
          unique: uuidv4(),
        },
      ];
      state.formData = initialState.formData;
    },

    resetMaterialRequestForm: (state, action) => {
      if (action.payload) {
        // if edit is true, reset to baseUnit data
        state.formData = state.materialRequest;
      } else {
        state.formData = initialState.formData; // if edit is false, reset to initial state
        state.materialRequestDetails = initialState.materialRequestDetails; // if edit is false, reset to initial state
      }
    },
    saveMaterialRequestForm: (state, action) => {
      state.formData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createMaterialRequest.pending, (state) => {
      state.loadingCreate = true;
    });
    builder.addCase(createMaterialRequest.fulfilled, (state, action) => {
      state.loadingCreate = false;
    });
    builder.addCase(createMaterialRequest.rejected, (state) => {
      state.edit = false;
      state.loadingCreate = false;
    });
    builder.addCase(getMaterialRequestById.pending, (state) => {
      state.loading = true;
      state.materialRequest = null;
    });
    builder.addCase(getMaterialRequestById.fulfilled, (state, action) => {
      state.edit = true;
      state.loading = false;
      state.materialRequest = action.payload.data;
      state.materialRequestDetails = action.payload.data?.purchaseOrderRequestDetails.map((value) => {
        return { ...value, unique: uuidv4() };
      });
      state.formData = {
        ...action.payload.data,
        isFromHo: materialRequestsFrom.find((value) => value.id === action.payload.data?.materialRequest?.isFromHo),
        requesterName: action.payload.data.requesterName.fullName || "",
      };
    });
    builder.addCase(getMaterialRequestById.rejected, (state) => {
      state.edit = false;
      state.loading = false;
      state.materialRequest = null;
    });

    builder.addMatcher(
      isAnyOf(
        getMaterialRequest.pending,
        getParticularPage.pending,
        getPrevious.pending,
        getNext.pending,
        handleSearch.pending
      ),
      (state) => {
        state.loadingMaterialRequest = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getMaterialRequest.fulfilled,
        getParticularPage.fulfilled,
        getPrevious.fulfilled,
        getNext.fulfilled,
        handleSearch.fulfilled
      ),
      (state, action) => {
        state.loadingMaterialRequest = false;
        state.materialRequests = action.payload.data;
        state.count = action.payload.totalCount;

        state.previous = action.payload.previous;
        state.next = action.payload.next;
      }
    );
    builder.addMatcher(
      isAnyOf(
        getMaterialRequest.rejected,
        getParticularPage.rejected,
        getPrevious.rejected,
        getNext.rejected,
        handleSearch.rejected
      ),
      (state) => {
        state.loadingMaterialRequest = false;
      }
    );
  },
});

export const {
  setShowCreateModal,
  setSaveAndContinue,
  goToPage,

  materialRequestDetailsUpdated,
  clearMaterialRequestDetails,
  clearAllMaterialRequest,
  saveMaterialRequestForm,
  resetMaterialRequestForm,
} = materialRequestSlice.actions;

export default materialRequestSlice.reducer;
